import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, Shield, ShieldOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function SuperadminUsers() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (q.trim()) params.q = q.trim();
      const { data: resp } = await api.get("/admin/users", { params });
      setData(resp);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, page]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSuperadmin = async (u) => {
    if (u.id === user?.id && u.is_superadmin) {
      const ok = window.confirm(
        "You are about to remove YOUR OWN superadmin role. You will lose access to this console immediately. Continue?"
      );
      if (!ok) return;
    }
    const r = window.prompt(
      u.is_superadmin
        ? `Demote ${u.email} from superadmin. Reason (audit log):`
        : `Promote ${u.email} to superadmin. Reason (audit log):`
    );
    if (!r || r.trim().length < 3) {
      toast.error("Reason required (3+ chars).");
      return;
    }
    setBusyId(u.id);
    try {
      await api.patch(`/admin/users/${u.id}`, {
        is_superadmin: !u.is_superadmin,
        reason: r.trim(),
      });
      toast.success(
        !u.is_superadmin ? "User promoted to superadmin." : "Superadmin removed."
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="superadmin-users">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl tracking-tight text-[#2C2A29]">
            Users
          </h2>
          <p className="font-sans text-sm text-[#7A7571] mt-1">
            {data.total} total &middot; promote a teammate to superadmin to give
            them access to this console.
          </p>
        </div>
      </div>

      <div className="relative max-w-md mb-4">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A5A29E]"
          strokeWidth={2}
        />
        <input
          data-testid="superadmin-users-search"
          type="text"
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
          placeholder="Search email or name…"
          className="w-full pl-9 pr-3 py-2 rounded-md border border-[#DCD7CE] bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30 focus:border-[#D96C4A]"
        />
      </div>

      <div
        className="rounded-xl border border-[#DCD7CE] bg-white overflow-hidden"
        data-testid="superadmin-users-table"
      >
        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#DCD7CE] bg-[#FAF9F7] font-sans text-[10px] uppercase tracking-[0.16em] text-[#7A7571] font-semibold">
          <div className="col-span-5">User</div>
          <div className="col-span-3">Workspaces</div>
          <div className="col-span-2">Created</div>
          <div className="col-span-2 text-right">Role</div>
        </div>
        {loading ? (
          <div
            className="px-4 py-10 font-sans text-sm text-[#7A7571] text-center"
            data-testid="superadmin-users-loading"
          >
            Loading users…
          </div>
        ) : data.items.length === 0 ? (
          <div
            className="px-4 py-10 font-sans text-sm text-[#7A7571] text-center"
            data-testid="superadmin-users-empty"
          >
            No users match.
          </div>
        ) : (
          data.items.map((u) => (
            <div
              key={u.id}
              data-testid={`user-row-${u.id}`}
              className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#EFEAE0] last:border-b-0 items-center"
            >
              <div className="col-span-5 min-w-0">
                <div className="font-sans text-sm text-[#2C2A29] font-semibold truncate flex items-center gap-2">
                  {u.display_name || u.email}
                  {u.is_superadmin ? (
                    <span
                      data-testid={`user-row-${u.id}-superadmin-badge`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#D96C4A]/15 text-[#8C3A1F]"
                    >
                      <Shield size={10} strokeWidth={2.5} />
                      Superadmin
                    </span>
                  ) : null}
                </div>
                <div className="font-sans text-xs text-[#7A7571] truncate">
                  {u.email}
                </div>
              </div>
              <div className="col-span-3 font-mono text-sm text-[#7A7571]">
                {u.workspace_count}
              </div>
              <div className="col-span-2 font-sans text-xs text-[#7A7571]">
                {u.created_at
                  ? new Date(u.created_at).toLocaleDateString()
                  : "—"}
              </div>
              <div className="col-span-2 flex justify-end">
                <button
                  onClick={() => toggleSuperadmin(u)}
                  disabled={busyId === u.id}
                  data-testid={`user-toggle-superadmin-${u.id}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-sans text-xs border transition-colors disabled:opacity-50 ${
                    u.is_superadmin
                      ? "border-[#8C3A1F] text-[#8C3A1F] bg-white hover:bg-[#8C3A1F]/10"
                      : "border-[#DCD7CE] text-[#2C2A29] bg-white hover:bg-[#EFEAE0]"
                  }`}
                >
                  {u.is_superadmin ? (
                    <>
                      <ShieldOff size={12} strokeWidth={2} />
                      Demote
                    </>
                  ) : (
                    <>
                      <Shield size={12} strokeWidth={2} />
                      Promote
                    </>
                  )}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
